import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { FileText, X } from 'lucide-react';
import type { ConvertibleProposalSummary } from '../lib/proposals';

interface ExistingCustomerProposalsModalProps {
  isOpen: boolean;
  proposals: ConvertibleProposalSummary[];
  customerName?: string;
  onIgnore: () => void;
  onConvert: (proposal: ConvertibleProposalSummary) => void;
  converting?: boolean;
}

function statusLabel(status: string) {
  switch (status) {
    case 'approved':
      return 'Approved';
    case 'viewed':
      return 'Viewed';
    case 'sent':
      return 'Sent';
    default:
      return status;
  }
}

function statusClass(status: string) {
  switch (status) {
    case 'approved':
      return 'bg-green-100 text-green-800';
    case 'viewed':
      return 'bg-blue-100 text-blue-800';
    case 'sent':
      return 'bg-amber-100 text-amber-800';
    default:
      return 'bg-gray-100 text-gray-700';
  }
}

export default function ExistingCustomerProposalsModal({
  isOpen,
  proposals,
  customerName,
  onIgnore,
  onConvert,
  converting = false,
}: ExistingCustomerProposalsModalProps) {
  useEffect(() => {
    if (!isOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [isOpen]);

  if (!isOpen || proposals.length === 0) return null;

  const single = proposals.length === 1 ? proposals[0] : null;

  return createPortal(
    <div
      className="fixed inset-0 z-[100300] flex items-end sm:items-center justify-center sm:p-4"
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
    >
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={converting ? undefined : onIgnore}
      />
      <div
        className="relative w-full sm:max-w-md bg-white rounded-t-2xl sm:rounded-2xl shadow-xl border border-gray-200 overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3 px-5 pt-5 pb-3">
          <div className="flex items-start gap-3 min-w-0">
            <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center flex-shrink-0">
              <FileText className="h-5 w-5 text-blue-600" />
            </div>
            <div className="min-w-0">
              <h3 className="text-base font-semibold text-gray-900">Existing proposal found</h3>
              <p className="text-sm text-gray-500 mt-0.5">
                {customerName
                  ? `${customerName} has ${proposals.length === 1 ? 'an open proposal' : `${proposals.length} open proposals`}. Convert it into this booking, or ignore and continue.`
                  : `This customer has ${proposals.length === 1 ? 'an open proposal' : `${proposals.length} open proposals`}. Convert into this booking, or ignore and continue.`}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onIgnore();
            }}
            disabled={converting}
            className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 disabled:opacity-50"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="px-5 pb-3 max-h-[40vh] overflow-y-auto space-y-2">
          {proposals.map((proposal) => (
            <div
              key={proposal.id}
              className="rounded-xl border border-gray-200 bg-gray-50 px-3.5 py-3"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-gray-900 truncate">
                    {proposal.title || 'Untitled proposal'}
                  </p>
                  <p className="text-xs text-gray-500 mt-0.5">
                    {[
                      proposal.proposal_number != null ? `#${proposal.proposal_number}` : null,
                      new Date(proposal.created_at).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric',
                      }),
                      proposal.total_amount != null
                        ? `$${Number(proposal.total_amount).toFixed(2)}`
                        : null,
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                  </p>
                </div>
                <span className={`shrink-0 px-2 py-0.5 rounded-full text-[11px] font-medium ${statusClass(proposal.status)}`}>
                  {statusLabel(proposal.status)}
                </span>
              </div>
              {!single && (
                <button
                  type="button"
                  disabled={converting}
                  onClick={() => onConvert(proposal)}
                  className="mt-2.5 w-full px-3 py-2 text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded-lg disabled:opacity-50 transition-colors"
                >
                  {converting ? 'Converting…' : 'Convert this proposal'}
                </button>
              )}
            </div>
          ))}
        </div>

        <div className="px-5 pb-5 pt-2 flex flex-col-reverse sm:flex-row gap-2 sm:justify-end">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onIgnore();
            }}
            disabled={converting}
            className="px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 rounded-xl disabled:opacity-50 transition-colors"
          >
            Ignore
          </button>
          {single && (
            <button
              type="button"
              onClick={() => onConvert(single)}
              disabled={converting}
              className="px-4 py-2.5 text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded-xl disabled:opacity-50 transition-colors"
            >
              {converting ? 'Converting…' : 'Convert proposal'}
            </button>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}
